import type { SupportedCurrencies } from "@/block-system/blocks/Form/Field/types";
import type { SupportedTableBlockFields } from "@/block-system/blocks/Table/lib/filterUnsupportedTableFields";
import { Badge } from "@/block-system/brickz/components/ui/badge";
import { Switch } from "@/block-system/brickz/components/ui/Switch";
import { ContinueZapButton } from "@/block-system/components/ContinueZapButton";
import { InteractButton } from "@/block-system/components/InteractButton";
import { useTrackingContext } from "@/observability/tracking/TrackingProvider";
import { usePageContext } from "@/lib/context/page-context";
import { renderCurrencyIcon } from "@/block-system/utilities/currency-helpers";
import { toast } from "@/components/Toaster/Toaster";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { CalendarIcon, CircleAlert, MailIcon, PhoneIcon } from "lucide-react";
import { useId, useState, type ComponentProps, type ReactNode } from "react";
import {
  type APIRecord,
  type APIRecordFieldInteraction,
} from "server/services/tables/types/records";
import { z } from "zod";
import { trpc } from "@/utils/trpc";
import { useInvalidateBlocksUsingTable } from "@/block-system/blocks/Table/lib/invalidateTableRecordQueries";
import { MIN_CHAR_LENGTH_FOR_SEE_MORE } from "@/block-system/blocks/Table/strings";
import { Button } from "@/block-system/brickz/components/ui/Button";
import { isBoolean } from "lodash";
import type { APIFieldLatest } from "@/server/services/tables/types/tables-latest";
import { cn } from "@/block-system/brickz/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/block-system/brickz/components/ui/tooltip";
import {
  aiFormulaFieldValueSchema,
  currencyFieldValueSchema,
  DateFieldTypeSchema,
  decimalFieldValueSchema,
  emailFieldValueSchema,
  labeledStringFieldValueSchema,
  linkedRecordFieldValueSchema,
  linkFieldValueSchema,
  multipleLabeledStringFieldValueSchema,
  multipleLinkedRecordFieldValueSchema,
  multipleLinkFieldValueSchema,
  multipleStringFieldValueSchema,
  numberFieldValueSchema,
  phoneNumberFieldValueSchema,
  stringFieldValueSchema,
  textFieldValueSchema,
} from "@/block-system/blocks/Table/Block/cellSchema";

type AllPossibleCellComponentProps = {
  field: APIFieldLatest;
  record: APIRecord;
  tableId: string;
  blockId: string;
  isDisabled: boolean;
  value: unknown;
  canUpdateRecords: boolean;
};

export const CellRenderers = {
  email: EmailFieldTypeCell,
  number: NumberFieldTypeCell,
  string: StringFieldTypeCell,
  phone_number: PhoneNumberFieldTypeCell,
  text: TextFieldTypeCell,
  decimal: DecimalFieldTypeCell,
  multiple_string: MultipleStringFieldTypeCell,
  multiple_labeled_string: MultipleLabeledStringFieldTypeCell,
  multiple_link: MultipleLinkFieldTypeCell,
  labeled_string: LabeledStringFieldTypeCell,
  multiple_linked_record: MultipleLinkedRecordFieldTypeCell,
  formula: FormulaFieldTypeCell,
  ai_formula: AIFormulaFieldTypeCell,
  button_trigger_zap: TriggerZapFieldTypeCell,
  button_continue_zap: ButtonContinueZapFieldTypeCell,
  boolean: BooleanFieldTypeCell,
  datetime: DatetimeFieldTypeCell,
  link: LinkFieldTypeCell,
  currency: CurrencyFieldTypeCell,
  linked_record: LinkedRecordFieldTypeCell,
  /**
   * The `any` here is on purpose.
   * We want TypeScript to _infer_ the type of props for a given cell renderer.
   *
   * If we were to use `AllPossibleCellComponentProps`, then we would have to
   * provide a type for every single cell renderer, and that would be a pain when writing tests.
   */
} satisfies Record<SupportedTableBlockFields, React.ComponentType<any>>;

function EmailFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = emailFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  return (
    <a
      className="flex flex-row items-center gap-1 text-sm font-normal leading-tight text-accent-foreground hover:underline"
      href={`mailto:${fieldValue}`}
    >
      <MailIcon className="size-3.5" color={"currentColor"} />
      {fieldValue}
    </a>
  );
}

export function NumberFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = numberFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  return <CellText>{fieldValue}</CellText>;
}

function StringFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = stringFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  return <CellText>{fieldValue}</CellText>;
}

function MultipleStringFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = multipleStringFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  return <CellText>{fieldValue.join(", ")}</CellText>;
}

function TextFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = textFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  return (
    <ReadMore text={fieldValue}>
      {(textToDisplay) => {
        return <CellText>{textToDisplay}</CellText>;
      }}
    </ReadMore>
  );
}

function DecimalFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = decimalFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  return <CellText>{fieldValue}</CellText>;
}

function PhoneNumberFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = phoneNumberFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  return (
    <div className="flex flex-row items-center gap-1 text-card-foreground dark:text-card-foreground-subtle">
      <PhoneIcon className="size-3.5" color={"currentColor"} />
      <CellText>{fieldValue}</CellText>
    </div>
  );
}

export function BooleanFieldTypeCell({
  tableId,
  blockId,
  record,
  field,
  value,
  canUpdateRecords,
}: Pick<
  AllPossibleCellComponentProps,
  "tableId" | "blockId" | "record" | "field" | "value" | "canUpdateRecords"
>) {
  const utils = trpc.useUtils();
  const invalidateBlocksUsingTable = useInvalidateBlocksUsingTable();
  const { emitConsumerInteractionEvent } = useTrackingContext();
  const { pageId, projectId, isEmbedded } = usePageContext();

  const updateRecordMutation = trpc.blockTable.updateRecord.useMutation({
    onSuccess() {
      toast.success({
        message: "Record updated successfully.",
      });

      emitConsumerInteractionEvent({
        interaction_goal: "Edit table record",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "datagrid",
        embedded_flag: isEmbedded,
      });

      void utils.blockKanban.get.invalidate();
      void invalidateBlocksUsingTable(tableId);
    },
  });

  const handleRecordUpdate = async (
    recordId: string,
    data: APIRecord["data"]
  ) => {
    await updateRecordMutation.mutateAsync({
      tableId: tableId,
      body: { updated_records: [{ id: recordId, data: data }] },
      tableBlockId: blockId,
    });
  };

  const switchValue = Boolean(value);
  const label = switchValue ? "Yes" : "No";
  const switchId = useId();

  const isDisabled = !canUpdateRecords || updateRecordMutation.isPending;

  return (
    <div className="flex items-center gap-2">
      <Switch
        size="small"
        checked={switchValue}
        id={switchId}
        disabled={isDisabled}
        onCheckedChange={(checked) => {
          if (!canUpdateRecords) {
            return;
          }

          void handleRecordUpdate(record.id, {
            ["f" + field.id]: checked,
          });
        }}
      />
      <label
        className="text-sm font-medium leading-tight text-card-foreground dark:text-card-foreground-subtle"
        htmlFor={switchId}
      >
        {label}
      </label>
    </div>
  );
}

interface TriggerZapButtonData {
  custom_label: string | undefined;
  is_enabled?: boolean;
}

export function TriggerZapFieldTypeCell({
  field,
  record,
  isDisabled,
  tableId,
}: Pick<
  AllPossibleCellComponentProps,
  "tableId" | "blockId" | "record" | "field" | "value" | "isDisabled"
>) {
  const fieldDataId = `f${field.id}`;
  const recordData = record.data[fieldDataId] as TriggerZapButtonData | null;

  // Tables allow fields to have a default behavior that can be overridden on the record level
  // so we first check for a setting on the record, and if there isn't one we default to
  // the field setting
  const isRecordButtonEnabled = isBoolean(recordData?.is_enabled)
    ? recordData?.is_enabled
    : field.options?.button?.is_enabled;

  let isButtonEnabled = !field.config?.locked && isRecordButtonEnabled;

  // In case the button is set to be disabled on click, we get the information from the record
  if (recordData) {
    const data = recordData as APIRecordFieldInteraction;
    if (data?.is_enabled === false) {
      isButtonEnabled = false;
    }
  }

  const disableOnClick = field.options?.button?.disable_on_click ?? false;
  const customLabel = recordData?.custom_label;

  return (
    <div key={field.id}>
      <InteractButton
        tableId={tableId}
        fieldId={field.id}
        recordId={record.id}
        isEnabled={isButtonEnabled}
        isDisabled={isDisabled}
        disableOnClick={disableOnClick}
      >
        {customLabel ?? field.options?.button?.label}
      </InteractButton>
    </div>
  );
}

function ButtonContinueZapFieldTypeCell({
  field,
  record,
  tableId,
  blockId,
}: Pick<
  AllPossibleCellComponentProps,
  "tableId" | "blockId" | "record" | "field" | "value"
>) {
  return (
    <ContinueZapButton
      field={field}
      record={record}
      tableId={tableId}
      blockId={blockId}
    />
  );
}

export function CurrencyFieldTypeCell({
  field,
  value,
}: Pick<AllPossibleCellComponentProps, "field" | "value">) {
  const parsedValue = currencyFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;
  const currencyFormat = field.options?.currency_format;
  if (!currencyFormat) {
    return <CellText>{fieldValue}</CellText>;
  }

  const currencyIcon = renderCurrencyIcon(
    currencyFormat as SupportedCurrencies
  );

  return (
    <div className="flex flex-row items-center gap-1">
      <Badge variant={"secondary"}>{currencyIcon}</Badge>
      <CellText>{fieldValue}</CellText>
    </div>
  );
}

export function DatetimeFieldTypeCell({
  field,
  value,
}: Pick<AllPossibleCellComponentProps, "field" | "value">) {
  const parsedValue = DateFieldTypeSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  const defaultDateFormat = "yyyy-MM-dd";
  const defaultTimeFormat = "HH:mm";
  const defaultTimezone = "UTC";

  const date = parseISO(fieldValue);
  const options = field.options ?? {};

  const dateFormat = options.date_format ?? defaultDateFormat;
  const timeFormat = options.include_time
    ? (options.time_format ?? defaultTimeFormat)
    : "";

  const dateTimeFormat = `${dateFormat} ${timeFormat}`;
  const timezone = options.timezone ?? defaultTimezone;

  const formattedDate = formatInTimeZone(date, timezone, dateTimeFormat);

  return (
    <div className="flex flex-row items-center gap-1 text-card-foreground dark:text-card-foreground-subtle">
      <CalendarIcon className="size-3.5" color="currentColor" />
      <CellText data-testid="datetime-field-type-cell-value">
        {formattedDate}
      </CellText>
    </div>
  );
}

function LabeledStringFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = labeledStringFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;
  return <CellText>{fieldValue.label}</CellText>;
}

function MultipleLabeledStringFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = multipleLabeledStringFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  const formattedValue = fieldValue.map(({ label }) => label).join(", ");
  return <CellText>{formattedValue}</CellText>;
}

const formulaFieldValueSchema = z.object({
  value: z.union([z.string(), z.number(), z.boolean()]),
});

export function FormulaFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const formulaErrorText = "Formula error";
  const parsedValue = formulaFieldValueSchema.safeParse(value);

  if (!parsedValue.success) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <span className="sr-only">{formulaErrorText}</span>
              <CircleAlert className="text-sm text-destructive" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>{formulaErrorText}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const { data: fieldValue } = parsedValue;

  return (
    <CellText data-testid="formula-field-type-cell-value">
      {fieldValue.value.toString()}
    </CellText>
  );
}

function AIFormulaFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = aiFormulaFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  return (
    <ReadMore text={fieldValue.value}>
      {(textToDisplay) => {
        return <CellText>{textToDisplay}</CellText>;
      }}
    </ReadMore>
  );
}

function LinkedRecordFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = linkedRecordFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  return <CellText>{parsedValue.data.label}</CellText>;
}

function MultipleLinkedRecordFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = multipleLinkedRecordFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;

  const recordLabels = fieldValue.map(({ label }) => label);
  return <CellText>{recordLabels.join(", ")}</CellText>;
}

function LinkFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = linkFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { link, text } = parsedValue.data;
  return (
    <CellLink href={link} title={text} target="_blank" rel="noopener">
      {text}
    </CellLink>
  );
}

function MultipleLinkFieldTypeCell({
  value,
}: Pick<AllPossibleCellComponentProps, "value">) {
  const parsedValue = multipleLinkFieldValueSchema.safeParse(value);
  if (!parsedValue.success) {
    return null;
  }

  const { data: fieldValue } = parsedValue;
  return (
    <ul>
      {fieldValue.map((item, index) => {
        const isLastItem = index === fieldValue.length - 1;
        const hasMoreThanOneItem = fieldValue.length > 1;
        const shouldAddComma = hasMoreThanOneItem && !isLastItem;
        return (
          <li key={index} className="inline">
            <CellLink
              href={item.link}
              title={item.text}
              target="_blank"
              rel="noopener"
              className="overflow-auto break-all"
            >
              {item.text}
            </CellLink>
            {shouldAddComma ? ", " : null}
          </li>
        );
      })}
    </ul>
  );
}

function CellText({
  children,
  ...props
}: Omit<ComponentProps<"span">, "className">) {
  return (
    <span
      className="overflow-hidden text-ellipsis text-sm font-normal leading-tight text-card-foreground dark:text-card-foreground-subtle"
      {...props}
    >
      {children}
    </span>
  );
}

function CellLink({ children, className, ...props }: ComponentProps<"a">) {
  return (
    <a
      className={cn(
        "overflow-hidden text-ellipsis text-sm font-medium leading-normal text-accent-foreground hover:underline",
        className
      )}
      {...props}
    >
      {children}
    </a>
  );
}

function ReadMore({
  text,
  children,
}: {
  text: string;
  children: (textToDisplay: string) => ReactNode;
}) {
  const { textToDisplay, isLongText, expanded, toggleExpanded } =
    useReadMore(text);

  return (
    <div className="flex flex-col">
      <div>{children(textToDisplay)}</div>
      {isLongText ? (
        <Button
          variant={"link"}
          onClick={toggleExpanded}
          className="-ml-4 text-left"
        >
          {expanded ? "See less" : "See more"}
        </Button>
      ) : null}
    </div>
  );
}

const useReadMore = (text: string) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  const truncatedText = text.slice(0, MIN_CHAR_LENGTH_FOR_SEE_MORE);

  const isLongText = text.length > MIN_CHAR_LENGTH_FOR_SEE_MORE;
  const showWholeText = expanded || !isLongText;

  const textToDisplay = showWholeText ? text : `${truncatedText}...`;

  return { textToDisplay, isLongText, expanded, toggleExpanded };
};
